import {
	FocusEvent,
	ReactNode,
	forwardRef,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useField } from "formik";
import Select, {
	GroupBase,
	InputProps,
	MenuListProps,
	MenuProps,
	OptionProps,
	Props as SelectProps,
	StylesConfig,
	components as reactSelectComponents,
} from "react-select";
import classNames from "classnames";
import { InputCore } from "app/components/form/InputCore/InputCore";
import { FieldLabel } from "app/components/form/Field/Field";
import { makeStableClassName } from "app/components/form/utils/stableClassName";
import ArrowDownIcon from "app/icons/navigation/ArrowDownIcon";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import useOnDir, { DIRECTION } from "app/utils/hooks/useOnDir";
import { useResizeObserver } from "app/hooks";
import { Menu, MenuList, Option, ClearIndicator } from "./componentsOverrides";
import { ICON_SIZES } from "app/constants";
import "./ReactSelectField.scss";
import styles from "./ReactSelectField.module.scss";

const defaultBoxShadow = "0 8px 10px rgba(0, 0, 0, 0.06)";

// Hoisted so react-select never sees a new component type for these on every render — they're
// permanently no-ops (InputCore already renders its own chevron via trailingSlot), so a stable
// reference avoids remounting them on every keystroke/re-render.
const NullDropdownIndicator = () => null;
const NullIndicatorSeparator = () => null;

// react-select positions the menu from `.control`'s rect, which sits inside InputCore's own
// 8px padding + 1px border — insetting it 9px from the field's real visible box. Compensated
// in menuPortal below rather than restructuring `.control` to fill InputCore's border-box.
const INPUT_CORE_INSET = 9;

// menuPortal's default width also comes from the too-narrow `.control` rect, further narrowed by
// InputCore's trailing chevron + gap — corrected here so e.g. `menuWidth="100%"` resolves correctly.
const TRAILING_SLOT_WIDTH = 20;

// Preserves AdvancedSelectFormik's own layout/positioning concerns; only the control's chrome
// changed — InputCore now supplies border/background/radius instead of this function drawing it.
const getStyles = (
	isMobile: boolean,
	isRLT: boolean,
	popperWidth: number,
	mobileMenuWidth: number | undefined,
	menuPortalMargin: number,
	popperOffset: number[],
	menuMarginTop: number,
	menuListPadding: [string, string],
	menuListMaxHeight: number,
	menuBorder: string,
	menuBoxShadow: string | undefined,
	menuWidth: string | number
): StylesConfig<unknown, false, GroupBase<unknown>> => ({
	menuList: base => ({
		...base,
		minWidth: isMobile ? "none" : popperWidth,
		width: isMobile ? mobileMenuWidth : "unset",
		maxHeight: Math.min(
			menuListMaxHeight,
			typeof base?.maxHeight === "number" ? base.maxHeight : menuListMaxHeight
		),
		padding: isMobile ? menuListPadding[1] : menuListPadding[0],
	}),
	option: base => ({
		...base,
		border: "none",
		height: isMobile ? "auto" : "56px",
		display: "flex",
		alignItems: "center",
	}),
	menuPortal: provided => ({
		...provided,
		zIndex: 20,
		marginTop: menuPortalMargin,
		// Shift the menu back out to align with the field's real visible box instead of `.control`'s inset one — see INPUT_CORE_INSET above. Horizontal only: this style key has no resolved placement (unlike `menu` below), so a vertical correction here would be blind to direction.
		left: typeof provided.left === "number" ? provided.left - INPUT_CORE_INSET : provided.left,
		// Widen back out to the field's real visible width too — see TRAILING_SLOT_WIDTH above.
		// A menu/menuList that itself asks for e.g. `menuWidth="100%"` or a fixed pixel width is
		// still ultimately relative to this box, so this must be correct for any of those to work.
		width:
			typeof provided.width === "number"
				? provided.width + INPUT_CORE_INSET * 2 + TRAILING_SLOT_WIDTH
				: provided.width,
	}),
	menu: (provided, { placement }) => ({
		...provided,
		width: isMobile ? mobileMenuWidth : menuWidth,
		boxShadow: menuBoxShadow || defaultBoxShadow,
		borderRadius: "none",
		// eslint-disable-next-line no-nested-ternary
		...(Array.isArray(popperOffset) && popperOffset.length
			? isRLT
				? { right: popperOffset[0] || 0 }
				: { left: popperOffset[0] || 0 }
			: {}),
		// Only for "bottom": with "top" this would fight `bottom: '100%'` (react-select's own base style for that placement) and the marginBottom below, over-constraining the box's height.
		...(Array.isArray(popperOffset) &&
		popperOffset.length &&
		popperOffset[1] !== null &&
		placement !== "top"
			? { top: popperOffset[1] }
			: {}),
		// Pushes the menu away from the control on whichever side it actually opened, so an upward flip never shifts it into the control and covers the chevron.
		...(placement === "top"
			? { marginBottom: menuMarginTop + INPUT_CORE_INSET, marginTop: 0 }
			: { marginTop: menuMarginTop + INPUT_CORE_INSET, marginBottom: 0 }),
		border: menuBorder,
	}),
	// Neutralize react-select's chrome — InputCore owns border/background/radius. height: 100%
	// keeps `.control` from sizing to content and shifting the menu's anchor point.
	control: base => ({
		...base,
		height: "100%",
		minHeight: "unset",
		background: "transparent",
		border: "none",
		borderRadius: 0,
		boxShadow: "none",
	}),
	// Figma's Content block (node 19432:241738) has its own ±4px inset on top of InputCore's 8px
	// padding. Left goes through the same --field-input-inset escape hatch as its siblings.
	valueContainer: base => ({
		...base,
		paddingLeft: "var(--field-input-inset, 4px)",
		paddingRight: "4px",
	}),
	// fontSize matches TextInput.module.scss's 1.4rem — react-select's native <input> doesn't
	// inherit the page's base font-size (form-control UA-stylesheet quirk); left unset it renders
	// off-center in InputCore's vertically-centered box.
	input: base => ({
		...base,
		margin: 0,
		padding: 0,
		fontSize: "1.4rem",
	}),
	placeholder: base => ({
		...base,
		margin: 0,
		fontSize: "1.4rem",
	}),
	singleValue: base => ({
		...base,
		margin: 0,
		fontSize: "1.4rem",
	}),
	indicatorsContainer: base => ({
		...base,
		padding: 0,
	}),
});

type ReactSelectFieldProps<OptionType> = {
	id: string;
	name: string;
	// Optional, like AdvancedSelectFormik's `label` — compact "trigger" selects (QuotationLuggageSelect,
	// QuotationActivityDate, AlternativeDepartureCityList) are placeholder-only by design.
	label?: ReactNode;
	// External invalid override, OR'd with the internal Formik-derived one. Needed for cross-field
	// validation `useField(name)` can't see (e.g. QuotationActivityDate's duplicate-date check).
	invalid?: boolean;
	className?: string;
	isRequired?: boolean;
	isSearchable?: boolean;
	icon?: ReactNode;
	keyCountMinToOpenMenu?: number;
	popperWidth?: number;
	menuPortalMargin?: number;
	menuMarginTop?: number;
	captureMenuScroll?: boolean;
	menuShouldBlockScroll?: boolean;
	loading?: boolean;
	menuListMaxHeight?: number;
	onFocus?: () => void;
	onMenuOpen?: () => void;
	onMenuClose?: () => void;
	popperOffset?: number[];
	menuListPadding?: [string, string];
	isAutoScrollActive?: boolean;
	menuBorder?: string;
	menuBoxShadow?: string;
	menuWidth?: string | number;
	prefixLabel?: ReactNode;
	placeholder?: ReactNode;
	showPlaceholderOnlyOnFocus?: boolean;
	"data-testid"?: string;
} & Omit<
	SelectProps<OptionType, false, GroupBase<OptionType>>,
	"id" | "className" | "onFocus" | "value" | "onChange" | "styles" | "ref" | "placeholder"
>;

// Layer 2/3 adapter: same public contract/behavior as legacy AdvancedSelectFormik (see
// AdvancedSelectFormik.test.jsx). InputCore now owns the control's visuals (Figma node
// 18878:13124); react-select's own menu/interaction logic is entirely untouched.
const ReactSelectFieldInner = <OptionType,>(
	{
		id,
		name,
		label,
		className,
		isRequired,
		isSearchable = true,
		icon,
		keyCountMinToOpenMenu = 0,
		popperWidth = 200,
		components,
		menuPortalMargin = 20,
		menuMarginTop = 23,
		captureMenuScroll = true,
		menuShouldBlockScroll = false,
		loading,
		menuListMaxHeight = 485,
		onFocus: onFocusProp = () => {},
		onMenuOpen: onMenuOpenProp = () => {},
		onMenuClose = () => {},
		popperOffset = [],
		menuListPadding = ["8px 0", "0"] as [string, string],
		isAutoScrollActive,
		menuBorder = "none",
		menuBoxShadow,
		menuWidth = "fit-content",
		prefixLabel,
		placeholder,
		showPlaceholderOnlyOnFocus = false,
		isDisabled,
		invalid: externalInvalid,
		"data-testid": dataCy,
		...restProps
	}: ReactSelectFieldProps<OptionType>,
	ref: React.Ref<HTMLInputElement>
) => {
	const direction = useOnDir();
	const [field, meta, helpers] = useField(name);
	const { setValue, setTouched, setError } = helpers;
	const [targetValue, setTargetValue] = useState("");
	const { resolution } = useContext(AppGlobalsContext);

	const isRLT = direction === DIRECTION.RTL;
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const wrapperRef = useRef<HTMLDivElement>(null);
	// Tracked via ResizeObserver (reuses the shared hook, see app/hooks) instead of reading
	// wrapperRef.current?.offsetWidth directly in the render body: that forced a layout read on
	// every render and never reacted to an actual resize (e.g. a sidebar toggle narrowing the field).
	const { width: mobileMenuWidth } = useResizeObserver({ ref: wrapperRef });
	const [isSelectFocused, setIsSelectFocused] = useState(false);
	const [menuIsOpen, setMenuOpen] = useState(false);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const selectInstanceRef = useRef<any>(null);

	const fieldValue = field.value as OptionType | undefined;

	// Same predicate as AdvancedSelectFormik/SelectFormik — a selected value alone makes the
	// field "touched" independent of Formik's own meta.touched.
	const isTouched = Boolean((meta.touched && (!meta.error || targetValue !== "")) || fieldValue);
	const hasLabel = label !== undefined && label !== null;
	// No label to float above the value means nothing should shift for it either — see the
	// `label` prop's own comment above.
	const isLabelFloated = hasLabel && (isTouched || Boolean(loading));
	const shouldShowPlaceholder = isSelectFocused && !fieldValue && showPlaceholderOnlyOnFocus;
	// Matches SelectFormik's touched-gated error timing (not InputFormik's ungated one), OR'd with
	// the consumer-supplied `invalid` prop for cross-field validation (see prop doc above).
	const invalid = Boolean((meta.touched && meta.error) || externalInvalid);

	// "Latest ref" pattern — setTouched/setError already change reference on most renders (Formik
	// re-memoizes its helpers whenever the form's context value changes), which happened to mask
	// this closure's staleness most of the time; the previous version of this component still had
	// a narrow window — mount, or a render where neither helper changed — where a consumer's own
	// new onFocus instance would be silently dropped. Same fix as FormikTextField's setTouchedOnFocus.
	const onFocusPropRef = useRef(onFocusProp);
	onFocusPropRef.current = onFocusProp;

	const handleFocus = useCallback(() => {
		if (isSearchable) {
			setIsSelectFocused(true);
		}
		setTouched(true);
		setError(undefined);
		onFocusPropRef.current();
	}, [isSearchable, setTouched, setError]);

	const setUntouchedOnBlur = useCallback(
		(event: FocusEvent<HTMLInputElement>) => {
			if (isSearchable) {
				setIsSelectFocused(false);
			}
			if (event.target.value === "" || !fieldValue) {
				setTargetValue("");
				setTouched(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fieldValue, isSearchable]
	);

	const handleChange = useCallback((option: unknown) => {
		setValue(option as OptionType);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleInputChange = useCallback(
		(value: string) => {
			setMenuOpen(value.length >= keyCountMinToOpenMenu);
		},
		[keyCountMinToOpenMenu]
	);

	const extraProps: Record<string, unknown> = {};
	if (keyCountMinToOpenMenu > 0) {
		extraProps.menuIsOpen = menuIsOpen;
		extraProps.onInputChange = handleInputChange;
	}

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		setTargetValue((event.target as HTMLInputElement).value);
	};

	// `onMenuOpen` (unlike `onMenuClose`, which was already handled this way) used to be left
	// undestructured, so it fell into `restProps` and clobbered this handler via the {...restProps}
	// spread below — silently breaking a consumer's onMenuOpen (e.g. AlternativeDepartureCityList's
	// chevron-rotation/data-menu-open/scroll-auto-close all gate on menuIsOpen, which never flipped
	// true when the consumer's own onMenuOpen won the spread instead of this one).
	const handleMenuOpen = useCallback(() => {
		setMenuOpen(true);
		onMenuOpenProp();
	}, [onMenuOpenProp]);

	const handleMenuClose = useCallback(() => {
		setMenuOpen(false);
		onMenuClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onMenuClose]);

	const simulateOnBlurEvent = useCallback(() => {
		selectInstanceRef.current?.blur();
	}, []);

	// Read via refs (not as useCallback deps) so handleScroll stays referentially stable and the
	// window listener below is registered once for the component's lifetime, instead of being
	// torn down and re-added on every menu open/close (a very frequent interaction).
	const menuIsOpenRef = useRef(menuIsOpen);
	menuIsOpenRef.current = menuIsOpen;
	const isAutoScrollActiveRef = useRef(isAutoScrollActive);
	isAutoScrollActiveRef.current = isAutoScrollActive;

	const handleScroll = useCallback(() => {
		if (wrapperRef?.current && selectInstanceRef?.current && !isAutoScrollActiveRef.current) {
			const menuRect = selectInstanceRef.current.menuListRef?.getBoundingClientRect();

			if (menuRect) {
				const halfMenuHeight = menuRect.height / 2;
				const menuMidpoint = menuRect.top + halfMenuHeight;
				const isHalfMenuVisible = menuMidpoint > 0 && menuMidpoint < window.innerHeight;

				if (!isHalfMenuVisible && menuIsOpenRef.current) {
					setMenuOpen(false);
					simulateOnBlurEvent();
				}
			}
		}
	}, [simulateOnBlurEvent]);

	useEffect(() => {
		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [handleScroll]);

	const reactSelectStyles = useMemo(
		() =>
			getStyles(
				isMobile,
				isRLT,
				popperWidth,
				mobileMenuWidth,
				menuPortalMargin,
				popperOffset,
				menuMarginTop,
				menuListPadding,
				menuListMaxHeight,
				menuBorder,
				menuBoxShadow,
				menuWidth
			),
		[
			isMobile,
			isRLT,
			popperWidth,
			mobileMenuWidth,
			menuPortalMargin,
			popperOffset,
			menuMarginTop,
			menuListPadding,
			menuListMaxHeight,
			menuBorder,
			menuBoxShadow,
			menuWidth,
		]
	);

	const stableClassName = makeStableClassName(dataCy);
	// Same idea as stableClassName, for Cypress: the portaled menu-list and options carry no
	// data-testid of their own, so derive one from the consumer's (undefined when it has none).
	const stableTestId = (part: string) => (dataCy ? `${dataCy}-${part}` : undefined);

	// Stable across renders (deps [ref] only) — an inline ref callback would get a new identity
	// every render, making React call it with `null` then the instance again each time, which
	// flickers any consumer-supplied callback ref with null in between.
	const setSelectRef = useCallback(
		(selectRefCallback: { inputRef: HTMLInputElement | null } | null) => {
			selectInstanceRef.current = selectRefCallback;
			if (typeof ref === "function") {
				ref(selectRefCallback?.inputRef ?? null);
			} else if (ref) {
				(ref as React.MutableRefObject<HTMLInputElement | null>).current =
					selectRefCallback?.inputRef ?? null;
			}
		},
		[ref]
	);

	// Menu/menu-list/option are portaled to document.body, outside any page wrapper's own SCSS
	// scope — extends the DS-014 stable-class mechanism there. Memoized on dataCy alone so
	// react-select doesn't remount the menu subtree on every parent re-render.
	const menuComponents = useMemo(
		() => ({
			Menu: (props: MenuProps<OptionType, false, GroupBase<OptionType>>) => (
				<Menu {...props} stableClassName={stableClassName("react-select-field__menu")} />
			),
			MenuList: (props: MenuListProps<OptionType, false, GroupBase<OptionType>>) => (
				<MenuList
					{...props}
					stableClassName={stableClassName("react-select-field__menu-list")}
					stableTestId={stableTestId("menu-list")}
				/>
			),
			Option: (props: OptionProps<OptionType, false, GroupBase<OptionType>>) => (
				<Option
					{...props}
					stableClassName={stableClassName("react-select-field__option")}
					stableTestId={stableTestId("option")}
				/>
			),
			// react-select has no top-level `aria-required` prop (unlike aria-invalid/aria-label/
			// aria-live, which it does wire through) — the combobox's real <input> only gets it via
			// this override. Unknown extra props survive react-select's own prop-cleaning and land
			// on the native <input>, same mechanism as ClearIndicator's icon override.
			Input: (props: InputProps<OptionType, false, GroupBase<OptionType>>) => (
				<reactSelectComponents.Input {...props} aria-required={isRequired || undefined} />
			),
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dataCy, isRequired]
	);

	// Merges the memoized menu overrides with the (already-stable) hoisted no-op indicators and
	// any consumer-supplied `components` override, so react-select receives a stable `components`
	// object instead of a fresh one (and fresh inline DropdownIndicator/IndicatorSeparator) every
	// render.
	const selectComponents = useMemo(
		() => ({
			...menuComponents,
			ClearIndicator,
			DropdownIndicator: NullDropdownIndicator,
			IndicatorSeparator: NullIndicatorSeparator,
			...components,
		}),
		[menuComponents, components]
	);

	// `id` deliberately lives only on react-select's own combobox <input> (via `inputId` below),
	// not the outer wrapper — AdvancedSelectFormik put it on the wrapper, causing a duplicate-id bug.

	// "react-select-field" (plain, unhashed) is a second, deliberate class alongside the
	// CSS-Modules one, so ReactSelectField.scss can select this wrapper without the hashed name.
	const wrapperClassName = classNames(
		styles["react-select-field"],
		"react-select-field",
		className,
		{
			[styles["react-select-field--with-icon"]]: Boolean(icon),
			[styles["react-select-field--with-prefix"]]: Boolean(prefixLabel),
		}
	);

	if (loading) {
		return (
			<div className={wrapperClassName} data-testid={dataCy}>
				<div className={styles["react-select-field__loader"]}>
					<LoaderBar height={10} width="80%" />
					<LoaderBar height={10} />
				</div>
			</div>
		);
	}

	return (
		<div
			ref={wrapperRef}
			className={wrapperClassName}
			data-testid={dataCy}
			data-floated={isLabelFloated || undefined}
			data-menu-open={menuIsOpen || undefined}
		>
			{prefixLabel}
			{icon && <div className={styles["react-select-field__icon"]}>{icon}</div>}
			<InputCore
				invalid={invalid}
				disabled={isDisabled}
				data-floated={isLabelFloated || undefined}
				className={classNames(
					styles["react-select-field__core"],
					stableClassName("react-select-field__core")
				)}
				trailingSlot={
					<ArrowDownIcon
						rotated={menuIsOpen}
						size={ICON_SIZES.MD}
						className={styles["react-select-field__chevron"]}
					/>
				}
			>
				<Select
					ref={setSelectRef}
					inputId={id}
					isClearable={isSearchable && isSelectFocused}
					backspaceRemovesValue={true}
					className={classNames(
						"react-select-field__select",
						stableClassName("react-select-field__input")
					)}
					classNamePrefix="react-select-field__select"
					styles={
						reactSelectStyles as StylesConfig<OptionType, false, GroupBase<OptionType>>
					}
					menuPlacement="auto"
					maxMenuHeight={menuListMaxHeight}
					menuPosition={isMobile ? "fixed" : "absolute"}
					placeholder={
						showPlaceholderOnlyOnFocus && !shouldShowPlaceholder ? null : placeholder
					}
					blurInputOnSelect={true}
					captureMenuScroll={captureMenuScroll}
					menuShouldScrollIntoView={false}
					menuShouldBlockScroll={menuShouldBlockScroll}
					{...field}
					value={fieldValue}
					onFocus={handleFocus}
					onBlur={setUntouchedOnBlur}
					onChange={handleChange}
					menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
					components={selectComponents}
					isSearchable={isSearchable}
					isDisabled={isDisabled}
					onKeyDown={handleKeyDown}
					onMenuOpen={handleMenuOpen}
					onMenuClose={handleMenuClose}
					{...(restProps as SelectProps<OptionType, false, GroupBase<OptionType>>)}
					{...extraProps}
				/>
				{hasLabel && (
					<FieldLabel
						htmlFor={id}
						error={invalid}
						required={isRequired}
						data-floated={isLabelFloated || undefined}
						className={classNames(
							styles["react-select-field__label"],
							stableClassName("react-select-field__label")
						)}
					>
						{label}
					</FieldLabel>
				)}
			</InputCore>
		</div>
	);
};

const ReactSelectField = forwardRef(ReactSelectFieldInner) as <OptionType>(
	props: ReactSelectFieldProps<OptionType> & { ref?: React.Ref<HTMLInputElement> }
) => ReturnType<typeof ReactSelectFieldInner>;

export { ReactSelectField };
